import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export const Menu = DropdownPrimitive.Root;
export const MenuTrigger = DropdownPrimitive.Trigger;

export function MenuContent({
  children,
  align = 'end',
  className,
}: {
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
}) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        align={align}
        sideOffset={8}
        className={cn(
          'border-line bg-surface-overlay z-50 min-w-52 overflow-hidden rounded-md border p-1 shadow-lg',
          'data-[state=open]:animate-pop',
          className,
        )}
      >
        {children}
      </DropdownPrimitive.Content>
    </DropdownPrimitive.Portal>
  );
}

export function MenuItem({
  children,
  onSelect,
  icon,
  tone = 'default',
  asChild,
  className,
}: {
  children: ReactNode;
  onSelect?: (event: Event) => void;
  icon?: ReactNode;
  tone?: 'default' | 'danger';
  asChild?: boolean;
  className?: string;
}) {
  return (
    <DropdownPrimitive.Item
      asChild={asChild}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-sm px-2.5 py-2 text-sm outline-none select-none',
        tone === 'danger'
          ? 'text-danger data-[highlighted]:bg-danger-soft'
          : 'text-text-muted data-[highlighted]:bg-primary-soft data-[highlighted]:text-primary',
        className,
      )}
    >
      {asChild ? (
        children
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </DropdownPrimitive.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="eyebrow px-2.5 pt-2 pb-1">{children}</div>;
}

export function MenuSeparator() {
  return <DropdownPrimitive.Separator className="bg-line my-1 h-px" />;
}

export const TooltipProvider = TooltipPrimitive.Provider;

export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="border-line bg-surface-overlay text-text-muted data-[state=delayed-open]:animate-fade-in z-50 max-w-64 rounded-sm border px-2.5 py-1.5 text-xs shadow-md"
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-surface-overlay" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
